import React, { useContext, useEffect, useState } from 'react'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import OButton from 'components/reusable/OButton'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import dayjs from 'dayjs'
import AuthContext from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import AttendanceReportTable from './AttendanceReportTable'
import { FiDownload } from 'react-icons/fi'
import Select from 'react-select'
import useToastContext from 'hooks/useToastContext'
import Pagination from '../Pagination'
import helpers from '../../utils/helpers'

const displayEmployeeId = (item) => {
  if (item?.employeeCode) return item.employeeCode
  const storedId = item?.employee?.employeeId
  if (storedId) return storedId
  const companyName = item?.companyName
  const employeeMongoId = item?.employee?._id
  if (companyName && employeeMongoId) {
    return `${companyName.substring(0, 3)}${String(employeeMongoId).slice(-3)}`
  }
  return '-'
}

const AttendanceReportModule = () => {
  const { updatePageName } = useContext(AuthContext)
  const notification = useToastContext()
  const { t } = useTranslation()

  const [employeeList, setEmployeeList] = useState([])
  const [finalEmployeeList, setFinalEmployeeList] = useState([])
  const [currentItem, setCurrentItem] = useState('')
  const [isEmployeeList, setIsEmployeeList] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // ── Pagination State ──────────────────────────────────────────────────────
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10,
    totalItems: 0
  })

  // ── Default: current month ───────────────────────────────────────────────
  const [filterData, setFilterData] = useState({
    category: [],
    searchKey: '',
    startDate: dayjs().startOf('month').toDate(),
    endDate: dayjs().endOf('month').toDate(),
    userIds: '',
    isReset: false,
    isFilter: false
  })

  const handleView = item => {
    setCurrentItem(item)
    setIsEmployeeList(prev => !prev)
  }

  // ── Pagination handler ──────────────────────────────────────────────────
  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  // ── Fetch attendance data ────────────────────────────────────────────────
  const allEmployeeList = async () => {
    setIsLoading(true)
    try {
      const { category, startDate, endDate } = filterData

      const payload = {
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : dayjs().endOf('month').format('YYYY-MM-DD'),
        page,
        limit: pageSize
      }

      // employeeIDs only sent when specific employees are selected
      const selectedIds = (category || []).map(d => d?.value).filter(Boolean)
      if (selectedIds.length > 0) {
        payload.employeeIDs = JSON.stringify(selectedIds)
      }

      const result = await apiGet(apiPath.weeklyTimeSheetListHR, payload)
      const resultStatus = result?.data?.success

      if (resultStatus) {
        const response = result?.data?.results
        const transformedData = transformWeeklyData(response?.docs || [])
        setEmployeeList(transformedData)
        
        // Update pagination object
        setPaginationObj({
          ...paginationObj,
          page: response.page || 1,
          pageCount: response.totalPages || 1,
          totalItems: response.totalDocs || 0,
          perPageItem: response.docs?.length || 0
        })

        if (transformedData.length === 0) {
          notification.info('No attendance records found for the selected period')
        }
      } else {
        setEmployeeList([])
        setPaginationObj({
          ...paginationObj,
          page: 1,
          pageCount: 1,
          totalItems: 0
        })
        if (selectedIds.length > 0) {
          notification.error(result?.data?.message)
        }
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error.message)
      setEmployeeList([])
      setPaginationObj({
        ...paginationObj,
        page: 1,
        pageCount: 1,
        totalItems: 0
      })
      notification.error('Error fetching attendance data')
    } finally {
      setIsLoading(false)
    }
  }

  // ── Transform API data for the table ────────────────────────────────────
  const transformWeeklyData = (data) => {
    if (!data?.length) return []

    return data
      .map(item => {
        let workTime = '0 hours 0 minutes 0 seconds'
        let breakTime = '0 hours 0 minutes 0 seconds'

        if (item.punchInDateTime && item.punchOutDateTime) {
          const workMs = dayjs(item.punchOutDateTime).diff(dayjs(item.punchInDateTime))

          let totalBreakMs = 0
          ;(item.breaks || []).forEach(br => {
            if (br.startBreakTime && br.endBreakTime) {
              totalBreakMs += dayjs(br.endBreakTime).diff(dayjs(br.startBreakTime))
            }
          })

          const actualMs = Math.max(0, workMs - totalBreakMs)

          const fmt = (ms) => {
            const secs = Math.floor(ms / 1000)
            const h = Math.floor(secs / 3600)
            const m = Math.floor((secs % 3600) / 60)
            const s = secs % 60
            return `${h} hours ${m} minutes ${s} seconds`
          }

          workTime = fmt(actualMs)
          breakTime = fmt(totalBreakMs)
        }

        return {
          _id: item._id,
          employee: {
            _id: item.employee?._id || null,
            name: item.employee?.name || '-',
            employeeId: displayEmployeeId(item)
          },
          employmentStatus: item.employmentStatus || null,
          companyName: item.companyName || null,
          employeeId: item.employeeId,
          createdAt: item.date || new Date(),
          punchInDateTime: item.punchInDateTime || null,
          punchOutDateTime: item.punchOutDateTime || null,
          punchInLocation: item.punchInLocation || null,
          punchOutLocation: item.punchOutLocation || null,
          breaks: item.breaks || [],
          regularHours: item.regularHours || 0,
          overtimeHours: item.overtimeHours || 0,
          isLate: item.isLate || false,
          isEarly: item.isEarly || false,
          isAbsent: item.isAbsent || false,
          attendanceStatus: item.attendanceStatus || null,
          status: item.status || '-',
          endBreak: null,
          workTimeDisplay: workTime,
          breakTimeDisplay: breakTime
        }
      })
      .sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)))
  }

  // ── Download Excel (Client-side) ─────────────────────────────────────────
  const handleDownload = async () => {
    try {
      const { category, startDate, endDate } = filterData
      const selectedIds = (category || []).map(d => d?.value).filter(Boolean)

      // Show loading state
      setIsLoading(true)
      notification.info('Preparing data for export...')

      // Fetch ALL data without pagination
      const payload = {
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : dayjs().endOf('month').format('YYYY-MM-DD'),
        bypassPagination: 'true', // ← IMPORTANT: Bypass pagination
        page: 1,
        limit: 10000 // Set a high limit as fallback
      }
      if (selectedIds.length > 0) payload.employeeIDs = JSON.stringify(selectedIds)

      const result = await apiGet(apiPath.weeklyTimeSheetListHR, payload)
      
      if (result?.data?.success) {
        const allData = result?.data?.results?.docs || []
        
        if (allData.length === 0) {
          notification.error('No data available to export')
          return
        }

        // Transform data for Excel export
        const excelData = allData.map(item => {
          // Calculate work and break times
          let workTime = '0 hours 0 minutes 0 seconds'
          let breakTime = '0 hours 0 minutes 0 seconds'

          if (item.punchInDateTime && item.punchOutDateTime) {
            const workMs = dayjs(item.punchOutDateTime).diff(dayjs(item.punchInDateTime))

            let totalBreakMs = 0
            ;(item.breaks || []).forEach(br => {
              if (br.startBreakTime && br.endBreakTime) {
                totalBreakMs += dayjs(br.endBreakTime).diff(dayjs(br.startBreakTime))
              }
            })

            const actualMs = Math.max(0, workMs - totalBreakMs)

            const fmt = (ms) => {
              const secs = Math.floor(ms / 1000)
              const h = Math.floor(secs / 3600)
              const m = Math.floor((secs % 3600) / 60)
              const s = secs % 60
              return `${h} hours ${m} minutes ${s} seconds`
            }

            workTime = fmt(actualMs)
            breakTime = fmt(totalBreakMs)
          }

          return {
            "Date": item.date ? dayjs(item.date).format('MM-DD-YYYY') : '-',
            "Employee Name": item.employee?.name || '-',
            "Employee ID": displayEmployeeId(item),
            "Punch In Time": item.punchInDateTime ? dayjs(item.punchInDateTime).format('h:mm:ss A') : '-',
            "Punch In Location": item.punchInLocation || '-',
            "Punch Out Time": item.punchOutDateTime ? dayjs(item.punchOutDateTime).format('h:mm:ss A') : '-',
            "Punch Out Location": item.punchOutLocation || '-',
            "Total Break Time": breakTime,
            "Working Hours": workTime,
            "Status": item.attendanceStatus || item.status || '-',
            "Is Late": item.isLate ? 'Yes' : 'No',
            "Is Early": item.isEarly ? 'Yes' : 'No',
            "Is Absent": item.isAbsent ? 'Yes' : 'No',
          }
        })

        // Sort by date descending
        excelData.sort((a, b) => {
          const dateA = dayjs(a.Date, 'MM-DD-YYYY')
          const dateB = dayjs(b.Date, 'MM-DD-YYYY')
          return dateB.diff(dateA)
        })

        // Generate Excel file
        const XLSX = require('xlsx')
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(excelData)

        // Auto-size columns (optional)
        const colWidths = Object.keys(excelData[0] || {}).map(key => ({
          wch: Math.max(key.length, 20) // Minimum width of 20
        }))
        ws['!cols'] = colWidths

        XLSX.utils.book_append_sheet(wb, ws, 'Attendance')
        
        // Generate and download
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/octet-stream' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `attendance-report-${dayjs().format('YYYY-MM-DD')}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        notification.success(`Attendance report downloaded successfully (${excelData.length} records)`)
      } else {
        notification.error(result?.data?.message || 'Failed to fetch data for export')
      }
    } catch (error) {
      console.error('Download error:', error)
      notification.error('Error downloading report')
    } finally {
      setIsLoading(false)
    }
  }

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setFilterData({
      category: [],
      searchKey: '',
      startDate: dayjs().startOf('month').toDate(),
      endDate: dayjs().endOf('month').toDate(),
      userIds: '',
      isReset: true,
      isFilter: false
    })
    setPage(1)
  }

  // ── Fetch HR employee list for the dropdown ──────────────────────────────
  const hrEmployeeList = async () => {
    try {
      const result = await apiGet(apiPath.aremployeeListHR)
      if (result?.data?.success) {
        setFinalEmployeeList(
          result.data.results.map(item => ({  label: `${item.name} (${item.employeeId})`, value: item._id }))
        )
      }
    } catch (error) {
      console.error('Error fetching employee list:', error)
    }
  }

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => { updatePageName(t('Attendance Report')) }, [])
  useEffect(() => { hrEmployeeList() }, [])
  useEffect(() => { allEmployeeList() }, [filterData, page])

  const handleDateChange = (start, end) => {
    setPage(1)
    setFilterData(prev => ({ ...prev, startDate: start, endDate: end, isFilter: true, isReset: false }))
  }

  const handleEmployee = item => {
    setPage(1)
    setFilterData(prev => ({ ...prev, category: item, isFilter: true, isReset: false }))
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className='bg-[#F9F9F9]'>
      <div className='px-3 py-4'>
        <div className='bg-white border border-[#E9EDF9] rounded-lg'>
          <h2 className='px-4 py-3'>
            <strong>
              Attendance report for {dayjs(filterData.startDate).format('MMM D')} – {dayjs(filterData.endDate).format('MMM D, YYYY')}
            </strong>
          </h2>

          <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3'>
            <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
              <div className='flex items-center lg:pt-0 pt-3 flex-wrap'>
                <Select
                  isMulti
                  styles={{
                    control: base => ({
                      ...base,
                      padding: '0px 4px',
                      borderRadius: '0.5rem',
                      border: '2px solid rgb(223 223 223)',
                      width: '200px',
                      marginBlockEnd: '10px'
                    })
                  }}
                  name='category'
                  options={finalEmployeeList}
                  value={filterData.category || null}
                  placeholder={
                    <span style={{ fontSize: '0.875rem' }} className='text-[#A5A5A5]'>
                      {t('ALL')} <span className='px-1 text-red-500'>*</span>
                    </span>
                  }
                  className='basic-multi-select'
                  classNamePrefix='select'
                  onChange={handleEmployee}
                />
                <ODateRangePicker
                  handleDateChange={handleDateChange}
                  isReset={filterData?.isReset}
                  setIsReset={setFilterData}
                />
                <OButton
                  label='Reset'
                  type='button'
                  onClick={handleReset}
                  extraClasses='ml-3 mb-3'
                  style={{ borderRadius: '0.5rem' }}
                />
              </div>
            </div>

            <div className='flex items-center justify-end px-4 ms-auto mb-3'>
              <button
                type='button'
                title={t('EXPORT')}
                className='bg-LightBlue flex text-sm px-8 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap'
                onClick={handleDownload}
                disabled={isLoading}
              >
                <FiDownload className='mr-3' />
                {isLoading ? 'Loading...' : t('EXPORT')}
              </button>
            </div>
          </form>

          {isLoading ? (
            <div className='p-4 text-center text-gray-500'>Loading attendance data...</div>
          ) : employeeList.length > 0 ? (
            <AttendanceReportTable
              employeeList={employeeList}
              allEmployeeList={allEmployeeList}
              handleView={handleView}
              currentItem={currentItem}
              isEmployeeList={isEmployeeList}
              page={page}           // ← THIS IS MISSING! Add this line
              pageSize={pageSize}   // ← Add this too
            />
          ) : (
            <div className='p-4 text-center text-gray-500'>
              No attendance records found for the selected period
            </div>
          )}

          {/* ── Pagination ─────────────────────────────────────────────────── */}
          {paginationObj?.totalItems > 0 && (
            <Pagination
              page={page}
              handlePageClick={handlePageClick}
              options={paginationObj}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default AttendanceReportModule